import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth, User } from 'firebase/auth';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, firebaseAppId } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faSave,
  faTimesCircle,
  faUser,
  faSpinner,
  faIdCard,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faCheckCircle,
  faCity,
  faBuilding,
  faInfoCircle,
  faMapMarkerAlt,
  faGlobe,
  faCreditCard,
  faStore,
  faImage,
  faTag,
  faUserCircle,
  faMoneyBill,
  faClock,
  faLink,
  faHashtag
} from '@fortawesome/free-solid-svg-icons';
import { FormGroup } from '../auths/styles/Signup.styles';

const appId = firebaseAppId;

interface UserProfileData {
  // Original fields
  name: string;
  location: string;
  profilePhotoUrl: string;
  stallNumber: string;
  email: string;
  phoneNumber: string;
  createdAt: Date | null;
  status: 'pending' | 'approved' | 'rejected' | string;
  cityTown: string;
  termsAccepted: boolean;
  businessName: string;
  
  // New fields from your data
  actualLocation: string;
  additionalDetails: string;
  businessPhone: string;
  businessPhotoPreview: string;
  category: string;
  contactPerson: string;
  facebookLink: string;
  googleMapLocation: string;
  instagramLink: string;
  isActive: boolean;
  latitude: number;
  longitude: number;
  paymentStatus: string;
  selectedCountry: string;
  selectedDistrict: string;
  selectedParish: string;
  selectedRegion: string;
  selectedSubcounty: string;
  selectedVillage: string;
  stallType: string;
  subscriptionAmount: number;
  subscriptionDuration: string;
  tiktokLink: string;
  transactionId: string | null;
  userlisting: string;
  website: string;
  whatsappPhone: string;
  xTwitterLink: string;
  youtubeLink: string;
}

// Styled Components
const ProfilePageContainer = styled.div`
  display: flex;
  justify-content: center;
  min-height: calc(100vh - 100px);
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const ProfileCard = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.white} 0%, #f8f9fa 100%);
  padding: ${({ theme }) => theme.spacing.xxl};
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xl};
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ProfilePhoto = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid ${({ theme }) => theme.colors.primary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const DefaultProfilePhoto = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 3rem;
  color: ${({ theme }) => theme.colors.white};
  border: 4px solid ${({ theme }) => theme.colors.primary};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ProfileInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primaryLight};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};

  & strong {
    color: ${({ theme }) => theme.colors.primary};
  }

  .icon {
    min-width: 20px;
    text-align: center;
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const Label = styled.span`
  font-weight: bold;
  min-width: 160px;
  text-align: right;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    text-align: left;
  }
`;

const Value = styled.span`
  flex-grow: 1;
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    text-align: left;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;
  resize: vertical;
  min-height: 80px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const ActionButtonContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 1.5rem;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ type: 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  margin-top: ${({ theme }) => theme.spacing.md};
  width: 100%;
  background-color: ${({ type, theme }) =>
    type === 'error' ? theme.colors.dangerLight : theme.colors.successLight};
  color: ${({ type, theme }) =>
    type === 'error' ? theme.colors.danger : theme.colors.success};
  border: 1px solid
    ${({ type, theme }) =>
      type === 'error' ? theme.colors.danger : theme.colors.success};
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: ${({ theme }) => theme.spacing.xl} auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 30px;

    &:before {
      position: absolute;
      content: '';
      height: 22px;
      width: 22px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
    }
  }

  input:checked + .slider {
    background-color: ${({ theme }) => theme.colors.primary};
  }

  input:checked + .slider:before {
    transform: translateX(30px);
  }
`;

// Component
const Profile = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [profileData, setProfileData] = useState<UserProfileData>({
    name: '',
    location: '',
    profilePhotoUrl: '',
    stallNumber: '',
    email: '',
    phoneNumber: '',
    createdAt: null,
    status: '',
    cityTown: '',
    termsAccepted: false,
    businessName: '',
    actualLocation: '',
    additionalDetails: '',
    businessPhone: '',
    businessPhotoPreview: '',
    category: '',
    contactPerson: '',
    facebookLink: '',
    googleMapLocation: '',
    instagramLink: '',
    isActive: false,
    latitude: 0,
    longitude: 0,
    paymentStatus: '',
    selectedCountry: '',
    selectedDistrict: '',
    selectedParish: '',
    selectedRegion: '',
    selectedSubcounty: '',
    selectedVillage: '',
    stallType: '',
    subscriptionAmount: 0,
    subscriptionDuration: '',
    tiktokLink: '',
    transactionId: null,
    userlisting: '',
    website: '',
    whatsappPhone: '',
    xTwitterLink: '',
    youtubeLink: '',
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    const fetchUserProfile = async (currentUser: User) => {
      setLoading(true);
      setError(null);
      setMessage(null);
      try {
        const userDocRef = doc(
          db,
          'artifacts',
          appId,
          'users',
          currentUser.uid,
          'profile',
          'data'
        );
        const userDocSnap = await getDoc(userDocRef);

        let fetchedData: Partial<UserProfileData> = {};

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          fetchedData = {
            name: data.name || currentUser.displayName || '',
            location: data.location || '',
            profilePhotoUrl: data.profilePhotoUrl || currentUser.photoURL || '',
            stallNumber: data.stallNumber || '',
            email: data.email || currentUser.email || '',
            phoneNumber: data.phoneNumber || currentUser.phoneNumber || '',
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
            status: data.status || 'N/A',
            cityTown: data.cityTown || '',
            termsAccepted: typeof data.termsAccepted === 'boolean' ? data.termsAccepted : false,
            businessName: data.businessName || '',
            actualLocation: data.actualLocation || '',
            additionalDetails: data.additionalDetails || '',
            businessPhone: data.businessPhone || '',
            businessPhotoPreview: data.businessPhotoPreview || '',
            category: data.category || '',
            contactPerson: data.contactPerson || '',
            facebookLink: data.facebookLink || '',
            googleMapLocation: data.googleMapLocation || '',
            instagramLink: data.instagramLink || '',
            isActive: data.isActive || false,
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            paymentStatus: data.paymentStatus || '',
            selectedCountry: data.selectedCountry || '',
            selectedDistrict: data.selectedDistrict || '',
            selectedParish: data.selectedParish || '',
            selectedRegion: data.selectedRegion || '',
            selectedSubcounty: data.selectedSubcounty || '',
            selectedVillage: data.selectedVillage || '',
            stallType: data.stallType || '',
            subscriptionAmount: data.subscriptionAmount || 0,
            subscriptionDuration: data.subscriptionDuration || '',
            tiktokLink: data.tiktokLink || '',
            transactionId: data.transactionId || null,
            userlisting: data.userlisting || '',
            website: data.website || '',
            whatsappPhone: data.whatsappPhone || '',
            xTwitterLink: data.xTwitterLink || '',
            youtubeLink: data.youtubeLink || '',
          };
        } else {
          fetchedData = {
            name: currentUser.displayName || '',
            location: '',
            profilePhotoUrl: currentUser.photoURL || '',
            stallNumber: '',
            email: currentUser.email || '',
            phoneNumber: currentUser.phoneNumber || '',
            createdAt: null,
            status: 'pending',
            cityTown: '',
            termsAccepted: false,
            businessName: '',
            actualLocation: '',
            additionalDetails: '',
            businessPhone: '',
            businessPhotoPreview: '',
            category: '',
            contactPerson: '',
            facebookLink: '',
            googleMapLocation: '',
            instagramLink: '',
            isActive: false,
            latitude: 0,
            longitude: 0,
            paymentStatus: '',
            selectedCountry: '',
            selectedDistrict: '',
            selectedParish: '',
            selectedRegion: '',
            selectedSubcounty: '',
            selectedVillage: '',
            stallType: '',
            subscriptionAmount: 0,
            subscriptionDuration: '',
            tiktokLink: '',
            transactionId: null,
            userlisting: '',
            website: '',
            whatsappPhone: '',
            xTwitterLink: '',
            youtubeLink: '',
          };

          await setDoc(
            userDocRef,
            {
              ...fetchedData,
              createdAt: Timestamp.now(),
            },
            { merge: true }
          );
          fetchedData.createdAt = Timestamp.now().toDate();
        }
        setProfileData(fetchedData as UserProfileData);
      } catch (err: any) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load profile data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserProfile(user);
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleSave = async () => {
    if (!user) {
      setError('No user logged in.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const userDocRef = doc(
        db,
        'artifacts',
        appId,
        'users',
        user.uid,
        'profile',
        'data'
      );

      await setDoc(
        userDocRef,
        {
          name: profileData.name,
          location: profileData.location,
          profilePhotoUrl: profileData.profilePhotoUrl,
          stallNumber: profileData.stallNumber,
          email: profileData.email,
          phoneNumber: profileData.phoneNumber,
          cityTown: profileData.cityTown,
          businessName: profileData.businessName,
          actualLocation: profileData.actualLocation,
          additionalDetails: profileData.additionalDetails,
          businessPhone: profileData.businessPhone,
          businessPhotoPreview: profileData.businessPhotoPreview,
          category: profileData.category,
          contactPerson: profileData.contactPerson,
          facebookLink: profileData.facebookLink,
          googleMapLocation: profileData.googleMapLocation,
          instagramLink: profileData.instagramLink,
          isActive: profileData.isActive,
          latitude: profileData.latitude,
          longitude: profileData.longitude,
          paymentStatus: profileData.paymentStatus,
          selectedCountry: profileData.selectedCountry,
          selectedDistrict: profileData.selectedDistrict,
          selectedParish: profileData.selectedParish,
          selectedRegion: profileData.selectedRegion,
          selectedSubcounty: profileData.selectedSubcounty,
          selectedVillage: profileData.selectedVillage,
          stallType: profileData.stallType,
          subscriptionAmount: profileData.subscriptionAmount,
          subscriptionDuration: profileData.subscriptionDuration,
          tiktokLink: profileData.tiktokLink,
          transactionId: profileData.transactionId,
          userlisting: profileData.userlisting,
          website: profileData.website,
          whatsappPhone: profileData.whatsappPhone,
          xTwitterLink: profileData.xTwitterLink,
          youtubeLink: profileData.youtubeLink,
        },
        { merge: true }
      );

      setMessage('Profile updated successfully!');
      setMessageType('success');
      setEditing(false);
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    }
  };

  const handleCancel = () => {
    if (user) {
      setLoading(true);
      setError(null);
      setMessage(null);
      const fetchUserProfileOnCancel = async (currentUser: User) => {
        try {
          const userDocRef = doc(
            db,
            'artifacts',
            appId,
            'users',
            currentUser.uid,
            'profile',
            'data'
          );
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            setProfileData({
              name: data.name || currentUser.displayName || '',
              location: data.location || '',
              profilePhotoUrl: data.profilePhotoUrl || currentUser.photoURL || '',
              stallNumber: data.stallNumber || '',
              email: data.email || currentUser.email || '',
              phoneNumber: data.phoneNumber || currentUser.phoneNumber || '',
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
              status: data.status || 'N/A',
              cityTown: data.cityTown || '',
              termsAccepted: typeof data.termsAccepted === 'boolean' ? data.termsAccepted : false,
              businessName: data.businessName || '',
              actualLocation: data.actualLocation || '',
              additionalDetails: data.additionalDetails || '',
              businessPhone: data.businessPhone || '',
              businessPhotoPreview: data.businessPhotoPreview || '',
              category: data.category || '',
              contactPerson: data.contactPerson || '',
              facebookLink: data.facebookLink || '',
              googleMapLocation: data.googleMapLocation || '',
              instagramLink: data.instagramLink || '',
              isActive: data.isActive || false,
              latitude: data.latitude || 0,
              longitude: data.longitude || 0,
              paymentStatus: data.paymentStatus || '',
              selectedCountry: data.selectedCountry || '',
              selectedDistrict: data.selectedDistrict || '',
              selectedParish: data.selectedParish || '',
              selectedRegion: data.selectedRegion || '',
              selectedSubcounty: data.selectedSubcounty || '',
              selectedVillage: data.selectedVillage || '',
              stallType: data.stallType || '',
              subscriptionAmount: data.subscriptionAmount || 0,
              subscriptionDuration: data.subscriptionDuration || '',
              tiktokLink: data.tiktokLink || '',
              transactionId: data.transactionId || null,
              userlisting: data.userlisting || '',
              website: data.website || '',
              whatsappPhone: data.whatsappPhone || '',
              xTwitterLink: data.xTwitterLink || '',
              youtubeLink: data.youtubeLink || '',
            });
          }
        } catch (err: any) {
          console.error('Error reverting profile data:', err);
          setError('Failed to revert profile data: ' + err.message);
        } finally {
          setLoading(false);
          setEditing(false);
          setMessage(null);
        }
      };
      fetchUserProfileOnCancel(user);
    } else {
      setEditing(false);
      setMessage(null);
    }
  };

  if (loading) {
    return (
      <ProfilePageContainer>
        <LoadingSpinner />
      </ProfilePageContainer>
    );
  }

  if (error) {
    return (
      <ProfilePageContainer>
        <ProfileCard>
          <StatusMessage type="error">{error}</StatusMessage>
          <p>Please try refreshing the page or logging in again.</p>
        </ProfileCard>
      </ProfilePageContainer>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <ProfilePageContainer>
      <ProfileCard>
        <ActionButtonContainer>
          {!editing ? (
            <IconButton onClick={() => setEditing(true)} aria-label="Edit Profile">
              <FontAwesomeIcon icon={faEdit} />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={handleSave} aria-label="Save Changes" disabled={loading}>
                <FontAwesomeIcon icon={faSave} />
              </IconButton>
              <IconButton onClick={handleCancel} aria-label="Cancel Edit" disabled={loading}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </IconButton>
            </>
          )}
        </ActionButtonContainer>

        <ProfileHeader>
          {profileData.profilePhotoUrl ? (
            <ProfilePhoto src={profileData.profilePhotoUrl} alt="Profile" />
          ) : (
            <DefaultProfilePhoto>
              <FontAwesomeIcon icon={faUser} size="4x" />
            </DefaultProfilePhoto>
          )}
          <h2>{profileData.name || 'No Name Set'}</h2>
          <p>{profileData.businessName}</p>
        </ProfileHeader>

        <ProfileInfo>
          {!editing ? (
            <>
              <SectionTitle>Personal Information</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <Label>Name:</Label>
                  <Value>{profileData.name || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <Label>Email:</Label>
                  <Value>{profileData.email || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <Label>Phone:</Label>
                  <Value>{profileData.phoneNumber || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faUserCircle} />
                  </span>
                  <Label>Contact Person:</Label>
                  <Value>{profileData.contactPerson || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <Label>Joined:</Label>
                  <Value>
                    {profileData.createdAt
                      ? profileData.createdAt.toLocaleDateString()
                      : 'N/A'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <Label>Status:</Label>
                  <Value>{profileData.status || 'N/A'}</Value>
                </InfoRow>
              </InfoGrid>

              <SectionTitle>Business Information</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faBuilding} />
                  </span>
                  <Label>Business Name:</Label>
                  <Value>{profileData.businessName || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faStore} />
                  </span>
                  <Label>Category:</Label>
                  <Value>{profileData.category || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <Label>Business Phone:</Label>
                  <Value>{profileData.businessPhone || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <Label>Stall Number:</Label>
                  <Value>{profileData.stallNumber || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faTag} />
                  </span>
                  <Label>Stall Type:</Label>
                  <Value>{profileData.stallType || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </span>
                  <Label>Active:</Label>
                  <Value>{profileData.isActive ? 'Yes' : 'No'}</Value>
                </InfoRow>
              </InfoGrid>

              <SectionTitle>Location Details</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Actual Location:</Label>
                  <Value>{profileData.actualLocation || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCity} />
                  </span>
                  <Label>City/Town:</Label>
                  <Value>{profileData.cityTown || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faGlobe} />
                  </span>
                  <Label>Country:</Label>
                  <Value>{profileData.selectedCountry || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Region:</Label>
                  <Value>{profileData.selectedRegion || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>District:</Label>
                  <Value>{profileData.selectedDistrict || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Subcounty:</Label>
                  <Value>{profileData.selectedSubcounty || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Parish:</Label>
                  <Value>{profileData.selectedParish || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Village:</Label>
                  <Value>{profileData.selectedVillage || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                  </span>
                  <Label>Coordinates:</Label>
                  <Value>
                    {profileData.latitude && profileData.longitude
                      ? `${profileData.latitude.toFixed(6)}, ${profileData.longitude.toFixed(6)}`
                      : 'N/A'}
                  </Value>
                </InfoRow>
              </InfoGrid>

              <SectionTitle>Subscription Details</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faMoneyBill} />
                  </span>
                  <Label>Subscription Amount:</Label>
                  <Value>
                    {profileData.subscriptionAmount
                      ? `$${profileData.subscriptionAmount}`
                      : 'N/A'}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <Label>Subscription Duration:</Label>
                  <Value>{profileData.subscriptionDuration || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCreditCard} />
                  </span>
                  <Label>Payment Status:</Label>
                  <Value>{profileData.paymentStatus || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faIdCard} />
                  </span>
                  <Label>Transaction ID:</Label>
                  <Value>{profileData.transactionId || 'N/A'}</Value>
                </InfoRow>
              </InfoGrid>

              <SectionTitle>Social Media & Links</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faGlobe} />
                  </span>
                  <Label>Website:</Label>
                  <Value>{profileData.website || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faLink} />
                  </span>
                  <Label>Facebook:</Label>
                  <Value>{profileData.facebookLink || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faLink} />
                  </span>
                  <Label>Instagram:</Label>
                  <Value>{profileData.instagramLink || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faLink} />
                  </span>
                  <Label>Twitter/X:</Label>
                  <Value>{profileData.xTwitterLink || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faLink} />
                  </span>
                  <Label>TikTok:</Label>
                  <Value>{profileData.tiktokLink || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faLink} />
                  </span>
                  <Label>YouTube:</Label>
                  <Value>{profileData.youtubeLink || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <Label>WhatsApp:</Label>
                  <Value>{profileData.whatsappPhone || 'N/A'}</Value>
                </InfoRow>
              </InfoGrid>

              <SectionTitle>Additional Information</SectionTitle>
              <InfoGrid>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <Label>Additional Details:</Label>
                  <Value>{profileData.additionalDetails || 'N/A'}</Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faImage} />
                  </span>
                  <Label>Business Photo:</Label>
                  <Value>
                    {profileData.businessPhotoPreview ? (
                      <img
                        src={profileData.businessPhotoPreview}
                        alt="Business"
                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                      />
                    ) : (
                      'N/A'
                    )}
                  </Value>
                </InfoRow>
                <InfoRow>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </span>
                  <Label>Terms Accepted:</Label>
                  <Value>{profileData.termsAccepted ? 'Yes' : 'No'}</Value>
                </InfoRow>
              </InfoGrid>
            </>
          ) : (
            <>
              <SectionTitle>Personal Information</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Name:</Label>
                  <Input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Your Name"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email:</Label>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                    placeholder="Your Email"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Phone:</Label>
                  <Input
                    type="tel"
                    value={profileData.phoneNumber}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phoneNumber: e.target.value })
                    }
                    placeholder="Your Phone Number"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Contact Person:</Label>
                  <Input
                    type="text"
                    value={profileData.contactPerson}
                    onChange={(e) =>
                      setProfileData({ ...profileData, contactPerson: e.target.value })
                    }
                    placeholder="Contact Person"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Profile Photo URL:</Label>
                  <Input
                    type="text"
                    value={profileData.profilePhotoUrl}
                    onChange={(e) =>
                      setProfileData({ ...profileData, profilePhotoUrl: e.target.value })
                    }
                    placeholder="Profile Photo URL"
                    disabled={loading}
                  />
                </FormGroup>
              </InfoGrid>

              <SectionTitle>Business Information</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Business Name:</Label>
                  <Input
                    type="text"
                    value={profileData.businessName}
                    onChange={(e) =>
                      setProfileData({ ...profileData, businessName: e.target.value })
                    }
                    placeholder="Business Name"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Category:</Label>
                  <Input
                    type="text"
                    value={profileData.category}
                    onChange={(e) => setProfileData({ ...profileData, category: e.target.value })}
                    placeholder="Business Category"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business Phone:</Label>
                  <Input
                    type="tel"
                    value={profileData.businessPhone}
                    onChange={(e) =>
                      setProfileData({ ...profileData, businessPhone: e.target.value })
                    }
                    placeholder="Business Phone"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Stall Number:</Label>
                  <Input
                    type="text"
                    value={profileData.stallNumber}
                    onChange={(e) =>
                      setProfileData({ ...profileData, stallNumber: e.target.value })
                    }
                    placeholder="Stall Number"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Stall Type:</Label>
                  <Input
                    type="text"
                    value={profileData.stallType}
                    onChange={(e) =>
                      setProfileData({ ...profileData, stallType: e.target.value })
                    }
                    placeholder="Stall Type"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Active:</Label>
                  <ToggleSwitch>
                    <input
                      type="checkbox"
                      checked={profileData.isActive}
                      onChange={(e) =>
                        setProfileData({ ...profileData, isActive: e.target.checked })
                      }
                      disabled={loading}
                    />
                    <span className="slider"></span>
                  </ToggleSwitch>
                </FormGroup>
              </InfoGrid>

              <SectionTitle>Location Details</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Actual Location:</Label>
                  <Input
                    type="text"
                    value={profileData.actualLocation}
                    onChange={(e) =>
                      setProfileData({ ...profileData, actualLocation: e.target.value })
                    }
                    placeholder="Actual Location"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>City/Town:</Label>
                  <Input
                    type="text"
                    value={profileData.cityTown}
                    onChange={(e) => setProfileData({ ...profileData, cityTown: e.target.value })}
                    placeholder="City/Town"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Country:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedCountry}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedCountry: e.target.value })
                    }
                    placeholder="Country"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Region:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedRegion}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedRegion: e.target.value })
                    }
                    placeholder="Region"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>District:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedDistrict}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedDistrict: e.target.value })
                    }
                    placeholder="District"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Subcounty:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedSubcounty}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedSubcounty: e.target.value })
                    }
                    placeholder="Subcounty"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Parish:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedParish}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedParish: e.target.value })
                    }
                    placeholder="Parish"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Village:</Label>
                  <Input
                    type="text"
                    value={profileData.selectedVillage}
                    onChange={(e) =>
                      setProfileData({ ...profileData, selectedVillage: e.target.value })
                    }
                    placeholder="Village"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Latitude:</Label>
                  <Input
                    type="number"
                    step="any"
                    value={profileData.latitude}
                    onChange={(e) =>
                      setProfileData({ ...profileData, latitude: parseFloat(e.target.value) })
                    }
                    placeholder="Latitude"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Longitude:</Label>
                  <Input
                    type="number"
                    step="any"
                    value={profileData.longitude}
                    onChange={(e) =>
                      setProfileData({ ...profileData, longitude: parseFloat(e.target.value) })
                    }
                    placeholder="Longitude"
                    disabled={loading}
                  />
                </FormGroup>
              </InfoGrid>

              <SectionTitle>Subscription Details</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Subscription Amount:</Label>
                  <Input
                    type="number"
                    value={profileData.subscriptionAmount}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        subscriptionAmount: parseFloat(e.target.value),
                      })
                    }
                    placeholder="Subscription Amount"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Subscription Duration:</Label>
                  <Select
                    value={profileData.subscriptionDuration}
                    onChange={(e) =>
                      setProfileData({ ...profileData, subscriptionDuration: e.target.value })
                    }
                    disabled={loading}
                  >
                    <option value="">Select Duration</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annual">Annual</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Payment Status:</Label>
                  <Input
                    type="text"
                    value={profileData.paymentStatus}
                    onChange={(e) =>
                      setProfileData({ ...profileData, paymentStatus: e.target.value })
                    }
                    placeholder="Payment Status"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Transaction ID:</Label>
                  <Input
                    type="text"
                    value={profileData.transactionId || ''}
                    onChange={(e) =>
                      setProfileData({ ...profileData, transactionId: e.target.value })
                    }
                    placeholder="Transaction ID"
                    disabled={loading}
                  />
                </FormGroup>
              </InfoGrid>

              <SectionTitle>Social Media & Links</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Website:</Label>
                  <Input
                    type="url"
                    value={profileData.website}
                    onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                    placeholder="Website URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Facebook:</Label>
                  <Input
                    type="url"
                    value={profileData.facebookLink}
                    onChange={(e) =>
                      setProfileData({ ...profileData, facebookLink: e.target.value })
                    }
                    placeholder="Facebook URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Instagram:</Label>
                  <Input
                    type="url"
                    value={profileData.instagramLink}
                    onChange={(e) =>
                      setProfileData({ ...profileData, instagramLink: e.target.value })
                    }
                    placeholder="Instagram URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Twitter/X:</Label>
                  <Input
                    type="url"
                    value={profileData.xTwitterLink}
                    onChange={(e) =>
                      setProfileData({ ...profileData, xTwitterLink: e.target.value })
                    }
                    placeholder="Twitter/X URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>TikTok:</Label>
                  <Input
                    type="url"
                    value={profileData.tiktokLink}
                    onChange={(e) =>
                      setProfileData({ ...profileData, tiktokLink: e.target.value })
                    }
                    placeholder="TikTok URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>YouTube:</Label>
                  <Input
                    type="url"
                    value={profileData.youtubeLink}
                    onChange={(e) =>
                      setProfileData({ ...profileData, youtubeLink: e.target.value })
                    }
                    placeholder="YouTube URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>WhatsApp:</Label>
                  <Input
                    type="tel"
                    value={profileData.whatsappPhone}
                    onChange={(e) =>
                      setProfileData({ ...profileData, whatsappPhone: e.target.value })
                    }
                    placeholder="WhatsApp Number"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Google Map Location:</Label>
                  <Input
                    type="url"
                    value={profileData.googleMapLocation}
                    onChange={(e) =>
                      setProfileData({ ...profileData, googleMapLocation: e.target.value })
                    }
                    placeholder="Google Maps URL"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Business Photo URL:</Label>
                  <Input
                    type="url"
                    value={profileData.businessPhotoPreview}
                    onChange={(e) =>
                      setProfileData({ ...profileData, businessPhotoPreview: e.target.value })
                    }
                    placeholder="Business Photo URL"
                    disabled={loading}
                  />
                </FormGroup>
              </InfoGrid>

              <SectionTitle>Additional Information</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Additional Details:</Label>
                  <TextArea
                    value={profileData.additionalDetails}
                    onChange={(e) =>
                      setProfileData({ ...profileData, additionalDetails: e.target.value })
                    }
                    placeholder="Additional Details"
                    disabled={loading}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>User Listing:</Label>
                  <Input
                    type="text"
                    value={profileData.userlisting}
                    onChange={(e) =>
                      setProfileData({ ...profileData, userlisting: e.target.value })
                    }
                    placeholder="User Listing"
                    disabled={loading}
                  />
                </FormGroup>
              </InfoGrid>

              {/* Read-only fields for display only */}
              <SectionTitle>System Information</SectionTitle>
              <InfoGrid>
                <FormGroup>
                  <Label>Joined:</Label>
                  <Input
                    type="text"
                    value={
                      profileData.createdAt
                        ? profileData.createdAt.toLocaleDateString()
                        : 'N/A'
                    }
                    readOnly
                    disabled
                    style={{ cursor: 'not-allowed' }}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Status:</Label>
                  <Input
                    type="text"
                    value={profileData.status || 'N/A'}
                    readOnly
                    disabled
                    style={{ cursor: 'not-allowed' }}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Terms Accepted:</Label>
                  <Input
                    type="text"
                    value={profileData.termsAccepted ? 'Yes' : 'No'}
                    readOnly
                    disabled
                    style={{ cursor: 'not-allowed' }}
                  />
                </FormGroup>
              </InfoGrid>
            </>
          )}
        </ProfileInfo>
        {message && <StatusMessage type={messageType}>{message}</StatusMessage>}
      </ProfileCard>
    </ProfilePageContainer>
  );
};

export default Profile;